"use client";

import ProfilePicture from "@/components/common/profile-picture";
import UpVoteButton from "@/components/common/upvote-button/upvote-button";
import NotFound from "@/components/ui/not-found";
import useIsMobile from "@/hooks/useIsMobile";
import { LinkedInIcon } from "@/icons";
import { BASE_URL, VOTES_API } from "@/lib/constants";
import {
	formatImageUrl,
	formatURL,
	formatViews,
	formatYoutubeLink,
	handleTrackClickEvent,
} from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { TGetComments } from "@/types/comments";
import { TGetProductDetails } from "@/types/products";
import { useQuery } from "@tanstack/react-query";
import {
	ArrowRightIcon,
	CircleDollarSignIcon,
	EyeIcon,
	ImageIcon,
	MapPinIcon,
	PlusSquare,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import ProductTag from "../home/components/product-listing/components/product-tag";
import CommentInputField from "./components/comment-input-field";
import Comments from "./components/comments";
import FounderDetailsCard from "./components/founders-card";
import ProductDetailsLoading from "./components/product-details-loading";
import ProductFAQCard from "./components/product-faq-card";
import { ProductCarousel } from "./components/products-carousel";
import { useProductEngagement } from "@/hooks/useProductEngagement";
import Skeleton from "react-loading-skeleton";
import { ModalType } from "@/types/modal-type";

function formatFundingText(text: string) {
	const words = text.split("_");
	const titleCaseWords = words.map(
		(word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
	);
	return titleCaseWords.join(" ");
}

async function getProductSpecificVotesCount(slug: string) {
	if (!slug) {
		return { votesCount: 0, isUpvotedByUser: false };
	}

	const res = await fetch(`${VOTES_API}/${slug}`, { next: { revalidate: 3 } });
	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as {
		data: { votesCount: number; isUpvotedByUser?: boolean };
	};

	return data;
}

async function getProductSpecificComments(productSlug: string) {
	const url = new URL(`${BASE_URL}/api/comments`);
	url.searchParams.set("slug", productSlug);

	const res = await fetch(url);
	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as { data: TGetComments };
	return data;
}

const ProductDetails = ({
	slug,
	productDetails,
	type = "default",
	showDetailsButton,
}: {
	slug: string;
	productDetails: TGetProductDetails;
	type?: "default" | "preview";
	showDetailsButton?: boolean;
}) => {
	const { closeModal } = useModal();
	const { rating, reviewsCount } = useProductEngagement(slug);

	const {
		isLoading: isCommentsLoading,
		isError: isCommentsError,
		data: comments,
		error: commentsError,
	} = useQuery({
		queryKey: ["commentsProductSpecific"],
		queryFn: () => getProductSpecificComments(slug),
		refetchOnWindowFocus: false,
	});

	const {
		isLoading: isVotesDataLoading,
		data: votesData = { votesCount: 0, isUpvotedByUser: false },
	} = useQuery({
		queryKey: ["votesProductSpecific"],
		queryFn: () => getProductSpecificVotesCount(slug),
	});

	const isMobile = useIsMobile();
	const usersWhoVotedTheProduct =
		productDetails?.Vote && productDetails?.Vote.length > 0
			? isMobile
				? productDetails.Vote.slice(0, 4)
				: productDetails.Vote
			: [];

	if (isVotesDataLoading || isCommentsLoading) {
		return <ProductDetailsLoading />;
	}

	if (!productDetails) {
		return <NotFound />;
	}

	if (isCommentsError) {
		return <span>{(commentsError as { message: string }).message}</span>;
	}

	const formattedCategories = productDetails.parentCategories.map((category) => ({
		id: category.parentCategoryId,
		name: category.parentCategory.name,
	}));

	const logo = formatImageUrl(productDetails?.logo);
	const founders = productDetails?.Founders;
	const { productMetaData } = productDetails;

	const screenshots: string[] = [];
	productMetaData?.screenshots?.forEach((screenshot) => {
		const formattedImageUrl = formatImageUrl(screenshot);
		if (formattedImageUrl) {
			screenshots.push(formattedImageUrl);
		}
	});

	const websiteLink = formatURL(productMetaData?.websiteUrl);
	const linkedInLink = formatURL(productMetaData?.companyLinkedIn);
	const youtubeDemoVideoUrl = formatYoutubeLink(productMetaData?.demoVideo);

	return (
		<div>
			<div>
				<div className="mt-8 md:mt-10 lg:mt-12">
					<div className="gap-5 md:grid md:grid-cols-8">
						<div className="md:col-span-4 lg:col-span-5">
							<div className="h-28 w-28 overflow-hidden rounded-lg md:h-32 md:w-32">
								{logo ? (
									<Image
										src={logo}
										width={150}
										height={150}
										alt={productDetails.name}
										className="h-full w-full object-contain"
									/>
								) : (
									<ImageIcon className="h-40 w-40 text-gray-400" />
								)}
							</div>
							<div className="mb-4 mt-5">
								<h1 className="mb-1 text-2xl font-bold lg:text-3xl">{productDetails.name}</h1>
								<p className="mb-2 max-w-lg text-gray-500 lg:text-lg">{productDetails.tagline}</p>
							</div>
						</div>
						<div className="grid h-max grid-cols-4 gap-2 md:col-span-4 lg:col-span-3">
							<div className="col-span-3 flex flex-1 flex-col gap-3">
								{founders.map((founder) => (
									<FounderDetailsCard key={founder.id} {...founder} designation={founder.role} />
								))}
							</div>
							<div className="relative grid h-full min-h-[70px] w-full place-items-center overflow-hidden rounded-lg bg-brand-primary">
								<Image
									className="absolute -z-0 h-full w-full object-cover"
									src="/images/stripes-blue.png"
									alt="stripes"
									width={300}
									height={300}
								/>
								<span className="relative text-2xl font-medium text-white">
									{votesData.votesCount}
								</span>
							</div>
						</div>
					</div>
					<div className="my-8 flex flex-col gap-7 md:my-4 md:flex-row xl:my-2 xl:items-center">
						<div className="flex h-max flex-1 flex-wrap gap-2">
							<div className="flex h-max w-max cursor-default items-center gap-1 rounded border border-gray-200 bg-gray-100 px-2 py-1 text-gray-100">
								<MapPinIcon className="h-4 w-4 text-blue-500" />
								<p className="text-sm text-gray-500">
									{productDetails?.regions?.[0]?.region?.name}
								</p>
							</div>
							{formattedCategories.map((category) => (
								<ProductTag key={category.id} label={category.name} />
							))}
							{productMetaData?.stageOfFundraise &&
							productMetaData?.stageOfFundraise.toLowerCase() !== "bootstrapped" ? (
								<div className="flex h-max w-max cursor-default items-center gap-1 rounded border border-gray-200 bg-gray-100 px-2 py-1 text-gray-100">
									<CircleDollarSignIcon className="h-4 w-4 text-blue-500" />
									<p className="text-sm text-gray-500">
										{formatFundingText(productMetaData.stageOfFundraise)}
									</p>
								</div>
							) : null}
						</div>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-3 md:flex-1 md:gap-y-0 lg:justify-end">
							<div className="flex items-center justify-center gap-1 text-sm text-gray-500">
								<span>
									<EyeIcon className="h-4 w-4" />
								</span>
								<span>{formatViews(productDetails.viewsCount || 0)} Views</span>
							</div>
							{!productDetails.isClaimed && (
								<Link
									href="/contact-us"
									onClick={() => closeModal(ModalType.ProductDetails)}
									className="flex items-center justify-start gap-1 rounded-md bg-brand-primary px-6 py-3 text-sm text-white md:text-base"
								>
									<span className="font-semibold">Claim Products for Free</span>
									<span>
										<ArrowRightIcon className="h-4 w-4" />
									</span>
								</Link>
							)}
							{linkedInLink && productDetails.isClaimed ? (
								<Link
									onClick={() =>
										handleTrackClickEvent(linkedInLink, `${productDetails.name} LinkedIn`)
									}
									href={linkedInLink}
									className="rounded-lg p-4 transition hover:bg-gray-100 active:bg-gray-100 lg:py-3"
									target="_blank"
									rel="nofollow"
								>
									<LinkedInIcon className="h-5 w-5" />
								</Link>
							) : null}
							{websiteLink ? (
								<Link
									onClick={() =>
										handleTrackClickEvent(websiteLink, `${productDetails.name} Website`)
									}
									href={websiteLink}
									className="flex-1 rounded-md border border-gray-300 px-6 py-3 text-center font-medium transition hover:bg-gray-100 active:bg-gray-100 lg:flex-none"
									target="_blank"
									rel="nofollow"
								>
									Visit {productDetails.name}
								</Link>
							) : null}
							{type !== "preview" && !productDetails.isSponsored && productDetails.isClaimed ? (
								<UpVoteButton
									id={productDetails.id}
									name={productDetails.name}
									slug={productDetails.slug}
									upVotes={votesData?.votesCount}
									isUpvoted={votesData?.isUpvotedByUser}
									rating={rating}
									reviewsCount={reviewsCount}
									keyToInvalidate={["votes", slug]}
								/>
							) : null}
						</div>
					</div>
				</div>
				<div className="mt-8">
					<ProductCarousel
						showClaimProductBanner={!productDetails.isClaimed}
						video={youtubeDemoVideoUrl}
						images={screenshots}
						productSlug={productDetails.slug}
					/>
				</div>
				<div className="mt-2">
					<h2 className="w-full text-gray-600">{productDetails.description}</h2>
				</div>
				<div className="mt-8 flex items-center justify-between">
					{usersWhoVotedTheProduct.length > 0 ? (
						<div className="flex items-center justify-start gap-x-1">
							<div className="flex items-center -space-x-3 px-4">
								{usersWhoVotedTheProduct.map((userVote) =>
									userVote.isVerified ? (
										<Link
											key={userVote.id}
											href={userVote?.username ? `/${userVote.username}` : "#"}
											onClick={() => closeModal(ModalType.ProductDetails)}
										>
											<ProfilePicture
												profilePictureUrl={userVote.image || undefined}
												className={{
													container: "h-10 w-10 rounded-full bg-white",
													image: "rounded-full text-gray-600",
													badgeImage: "-bottom-4 h-8 w-8",
												}}
												isVerified={true}
												isFounder={userVote.isFounder}
											/>
										</Link>
									) : null
								)}
							</div>
							<p className="text-left text-lg font-semibold text-gray-800">
								<span className="text-xs text-brand-primary md:text-base">
									{productDetails.totalVotes}+ others
								</span>
							</p>
						</div>
					) : null}
					{showDetailsButton ? (
						<Link
							href={`/products/${productDetails.slug}`}
							onClick={() => closeModal(ModalType.ProductDetails, { disableURLChange: true })}
							className="flex items-center justify-start gap-1 rounded-md bg-brand-primary px-6 py-3 text-sm text-white md:text-base"
						>
							<span className="font-semibold">View Details</span>
							<span>
								<ArrowRightIcon className="h-4 w-4" />
							</span>
						</Link>
					) : null}
				</div>
				<div className="my-8 h-px w-full bg-gray-200" />
				{type === "preview" ? (
					<div>
						<div className="flex items-center justify-start gap-3 rounded-md border bg-transparent px-5 py-2 transition focus-within:border-blue-500">
							<div className="flex h-10 w-10 items-center justify-center rounded-full border font-bold capitalize">
								U
							</div>
							<input
								id="comment"
								type="text"
								name="comment"
								placeholder="Add a Comment"
								className="w-full bg-transparent px-2 text-sm outline-none transition-all duration-300 ease-in-out"
								required
							/>
							<button disabled>
								<PlusSquare className="h-6 w-6 text-brand-primary" />
							</button>
						</div>
					</div>
				) : (
					<CommentInputField productId={productDetails.id} />
				)}
				<h3 className="mb-6 mt-8 text-xl font-bold text-dark-primary md:text-2xl">
					More Product Details
				</h3>
				<ProductFAQCard
					question="What is the problem you are solving in the world?"
					answer={productMetaData?.problemSolving || "-"}
				/>
				<ProductFAQCard
					question="How are you solving it?"
					answer={productMetaData?.problemSolution || "-"}
				/>
				<ProductFAQCard
					question="What are your top features?"
					answer={productMetaData?.topFeatures || "-"}
				/>
				<ProductFAQCard
					question="What has been your biggest product accomplishment/ milestones?"
					answer={productMetaData?.productAccomplish || "-"}
				/>
				<ProductFAQCard
					question="Where do you need the most help with today?"
					answer={productMetaData?.helpNeeded || "-"}
				/>
				{type !== "preview" && (
					<>
						<div className="my-10">
							{isCommentsLoading ? (
								<Skeleton count={4} height={48} className="mb-6" />
							) : productDetails?.id ? (
								<Comments comments={comments} />
							) : null}
						</div>
						<CommentInputField productId={productDetails.id} />
					</>
				)}
				<div className="mb-8"></div>
			</div>
		</div>
	);
};

export default ProductDetails;
